import { useGetAvaliableSlots } from '@features/applicant/hooks/useGetAvaliableSlots';
import { useGetBookedAvaliableSlots } from '@features/applicant/hooks/useGetBookedAvaliableSlots';
import { useSignupInterview } from '@features/applicant/hooks/useSignupInterview';
import { useSignupUpdateInterview } from '@features/applicant/hooks/useSignupUpdateInterview';
import { Application } from '@features/applications';
import { getTimeZoneAbbreviation } from '@features/profile';
import { Close, Done } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Avatar,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  Chip,
} from '@mui/material';
import { lightGrey, primaryBlue } from '@styles/colors';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

type SelectedOption = {
  blockId: number | null;
  slotId: number | null;
  isSlot: boolean;
};

type AvailableSlotsProps = {
  isOpen: boolean;
  handleClose: () => void;
  application: Application;
  selectedSlot?: SelectedOption;
};
const AvailableSlots: FC<AvailableSlotsProps> = ({
  isOpen,
  handleClose,
  application,
  selectedSlot,
}) => {
  const browserTimeZone = getTimeZoneAbbreviation();
  const [errorText, setErrorText] = useState('');
  const [selectedOption, setSelectedOption] = useState<SelectedOption>(
    selectedSlot || {
      blockId: null,
      slotId: null,
      isSlot: false,
    },
  );
  const [blocksData, setBlocksData] = useState<any>(null);
  const { avaliableBlocks, isAvaliableBlocksLoading } = useGetAvaliableSlots({
    setErrorText,
    inviteId:
      application.calendar_block_invite && application.calendar_block_invite.id,
    jobListingId: application.job_listing.id,
  });
  const { avaliableBookedBlocks, isAvaliableBookedBlocksLoading } =
    useGetBookedAvaliableSlots({
      setErrorText,
      inviteId:
        application.calendar_block_invite &&
        application.calendar_block_invite.id,
      jobListingId: application.job_listing.id,
      enabled: Boolean(selectedSlot?.blockId),
    });

  const formatTimeRange = (
    startTime: string | number | Date | dayjs.Dayjs | null | undefined,
    endTime: string | number | Date | dayjs.Dayjs | null | undefined,
  ) => {
    return `${dayjs(startTime).format('h:mm A')} - ${dayjs(endTime).format('h:mm A')}`;
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMMM D, YYYY');
  };

  const { signupInterview, isSignupInterviewLoading } = useSignupInterview({
    setErrorText,
  });

  const { signupUpdateInterview, isSignupUpdateInterviewLoading } =
    useSignupUpdateInterview({
      setErrorText,
    });

  const handleChipClick = (
    blockId: number,
    slotId: number | null,
    isSlot: boolean,
  ) => {
    setSelectedOption({ blockId, slotId, isSlot });
  };

  const handleSubmit = () => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status === 200) {
          handleClose();
          toast.success(data.message);
        }
      },
    };
    if (
      selectedSlot &&
      selectedSlot.blockId === selectedOption.blockId &&
      selectedSlot.slotId === selectedOption.slotId
    ) {
      return toast.error('Please select a different slot');
    }
    const payload = {
      block_id: selectedOption.blockId,
      app_id: application.id,
      ...(selectedOption.slotId && { slot_id: selectedOption.slotId }),
      ...(selectedOption.isSlot && { is_slot: selectedOption.isSlot }),
    };
    if (selectedSlot?.blockId) {
      signupUpdateInterview(
        {
          ...payload,
          new_block_id: selectedOption.blockId,
          old_block_id: selectedSlot.blockId,
        },
        options,
      );
      return;
    }
    signupInterview(payload, options);
  };

  useEffect(() => {
    if (selectedSlot?.blockId) {
      setBlocksData(avaliableBookedBlocks);
      setSelectedOption(selectedSlot);
    } else {
      setBlocksData(avaliableBlocks);
    }
  }, [avaliableBlocks, avaliableBookedBlocks]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={() => handleClose()}
        >
          <Close />
        </IconButton>
        <br />
        {application.job_listing.company.s_logo_file && (
          <Avatar
            src={application.job_listing.company.s_logo_file}
            variant="square"
            sx={{
              borderRadius: 5,
              alignSelf: 'center',
              margin: 'auto',
              width: '60px',
              height: '60px',
            }}
          />
        )}
        <Typography variant="h4" sx={{ p: 3 }}>
          {application.job_listing.company.s_name}
        </Typography>
        <Typography variant="body1">
          {application.job_listing.company.s_city}{' '}
          {application.job_listing.company.s_state},{' '}
          {application.job_listing.company.s_zip}
        </Typography>
        <Typography variant="h5">Select an Interview Session</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mb={2}>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          {(isAvaliableBlocksLoading || isAvaliableBookedBlocksLoading) && (
            <CircularProgress />
          )}
          {blocksData &&
          (!isAvaliableBlocksLoading || !isAvaliableBookedBlocksLoading) &&
          blocksData?.object &&
          blocksData?.object?.length > 0 ? (
            <>
              {blocksData.object.map(
                (item: {
                  id: number;
                  slots: any[];
                  dt_start_time: string;
                  dt_end_time: string;
                  available_spots: number;
                  block_type: number;
                }) => (
                  <Stack spacing={2} key={item.id}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h6">
                        {formatDate(item.dt_end_time)} {browserTimeZone || ''} ({item?.block_type === 0 ? 'Virtual' : 'In Person'})
                      </Typography>
                      <Typography variant="h6">
                        {item.available_spots} Spots
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} gap={2} flexWrap="wrap">
                      {item.slots.length === 0 ? (
                        <Chip
                          label={formatTimeRange(
                            item.dt_start_time,
                            item.dt_end_time,
                          )}
                          clickable
                          sx={{
                            backgroundColor:
                              selectedOption.blockId === item.id &&
                              !selectedOption.slotId
                                ? primaryBlue
                                : '#f2f3f4',
                            color:
                              selectedOption.blockId === item.id &&
                              !selectedOption.slotId
                                ? 'white'
                                : 'black',
                            '&:hover': {
                              backgroundColor:
                                selectedOption.blockId === item.id &&
                                !selectedOption.slotId
                                  ? primaryBlue
                                  : lightGrey,
                            },
                            marginTop: 5,
                          }}
                          onClick={() => handleChipClick(item.id, null, false)}
                        />
                      ) : (
                        item.slots
                          .filter((slot) => {
                            if (
                              selectedSlot &&
                              slot.id === selectedSlot?.slotId
                            ) {
                              return true; // If slot.id matches selectedOption.slotId, include it without checking status
                            }
                            return slot.status === 1; // Otherwise, include it only if status is 1
                          })
                          .map(
                            (slot: {
                              id: number;
                              slot_start_time: string;
                              slot_end_time: string;
                            }) => (
                              <Chip
                                key={slot.id}
                                label={formatTimeRange(
                                  slot.slot_start_time,
                                  slot.slot_end_time,
                                )}
                                clickable
                                sx={{
                                  backgroundColor:
                                    selectedOption.blockId === item.id &&
                                    selectedOption.slotId === slot.id
                                      ? primaryBlue
                                      : '#f2f3f4',
                                  color:
                                    selectedOption.blockId === item.id &&
                                    selectedOption.slotId === slot.id
                                      ? 'white'
                                      : 'black',
                                  '&:hover': {
                                    backgroundColor:
                                      selectedOption.blockId === item.id &&
                                      selectedOption.slotId === slot.id
                                        ? primaryBlue
                                        : lightGrey,
                                  },
                                  marginTop: 5,
                                }}
                                onClick={() =>
                                  handleChipClick(item.id, slot.id, true)
                                }
                              />
                            ),
                          )
                      )}
                    </Stack>
                    <Divider />
                  </Stack>
                ),
              )}
            </>
          ) : (
            !isAvaliableBlocksLoading &&
            !isAvaliableBookedBlocksLoading &&
            blocksData?.object?.length == 0 && (
              <Typography variant="h6" sx={{ textAlign: 'center' }}>
                No available slots
              </Typography>
            )
          )}
          <LoadingButton
            variant="contained"
            color="success"
            onClick={handleSubmit}
            sx={{ marginTop: 2 }}
            startIcon={<Done />}
            loading={isSignupInterviewLoading || isSignupUpdateInterviewLoading}
            disabled={
              isSignupInterviewLoading ||
              isSignupUpdateInterviewLoading ||
              !selectedOption.blockId
            }
          >
            Signup
          </LoadingButton>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default AvailableSlots;
